/**
 * Penjaga "absen pulang terlalu cepat" — MURNI, tanpa React dan tanpa jam sistem.
 *
 * ADA KARENA INI SUDAH TERJADI. 31 Agustus 2026, Jihan absen masuk 07.51.27 dan absen pulang
 * 07.51.55 — **28 detik**. Dua event sungguhan, keduanya lokasi terverifikasi; bukan duplikat,
 * bukan salah render. Hari kerjanya tercatat `PRESENT` dengan **nol menit kerja**, dan tidak ada
 * satu pun lapisan yang berkeberatan: tidak ada aturan durasi minimum di mana pun.
 *
 * KONFIRMASI, BUKAN PENOLAKAN. Ada pekerjaan yang memang sebentar — antar satu paket lalu pulang,
 * atau masuk sebentar untuk mengambil barang. Server yang menolak akan membuat orang seperti itu
 * TIDAK BISA absen pulang sama sekali, dan yang mereka lakukan berikutnya adalah membiarkan
 * sesinya menggantung sampai tengah malam. Itu lebih buruk daripada nol menit: ia merusak
 * hari berikutnya juga.
 *
 * Karena itu penjaganya di klien dan berbentuk pertanyaan. Yang dicegah bukan absen pulang yang
 * singkat, melainkan absen pulang yang TIDAK DIMAKSUDKAN.
 */

/**
 * Di bawah ini dianggap layak ditanyakan.
 *
 * Lima menit, bukan satu: kasus yang memicu ini 28 detik, tetapi mis-tap tidak selalu secepat itu
 * — orang membaca layar konfirmasi dulu, berpindah tab, lalu menekan. Ambang satu menit akan
 * melewatkan sebagian besarnya. Ongkos salah tanya cuma satu ketukan; ongkos tidak bertanya
 * adalah satu hari kerja yang tercatat nol.
 */
export const AMBANG_DETIK = 5 * 60;

export interface PutusanPulang {
  /** Benar bila pengguna sebaiknya ditanya lebih dulu. */
  readonly perluKonfirmasi: boolean;
  /** Detik sejak absen masuk. `null` bila tidak dapat dihitung. */
  readonly detik: number | null;
  /** Kalimat siap tampil. Kosong bila tidak perlu konfirmasi. */
  readonly pesan: string;
}

const TIDAK_PERLU: PutusanPulang = { perluKonfirmasi: false, detik: null, pesan: '' };

/** "28 detik" · "3 menit" — satuan yang dipakai orang, bukan angka mentah. */
export function lamaSingkat(detik: number): string {
  if (detik < 60) return `${detik} detik`;
  const menit = Math.floor(detik / 60);
  return `${menit} menit`;
}

/**
 * Apakah absen pulang ini layak ditanyakan lebih dulu.
 *
 * @param firstCheckInAt jam masuk hari ini menurut SERVER (`today.firstCheckInAt`), bukan jam
 *   yang diingat layar. Keadaan yang dijaga sering melibatkan halaman yang dimuat ulang, dan
 *   ingatan layar hilang tepat pada saat ia paling dibutuhkan.
 * @param sekarang jam saat ini — disuntikkan supaya fungsi ini dapat diuji tanpa jam sistem.
 */
export function periksaPulangCepat(
  firstCheckInAt: string | null | undefined,
  sekarang: Date,
): PutusanPulang {
  if (!firstCheckInAt) return TIDAK_PERLU;

  const masuk = new Date(firstCheckInAt).getTime();
  if (Number.isNaN(masuk)) return TIDAK_PERLU;

  const selisihDetik = Math.floor((sekarang.getTime() - masuk) / 1000);

  /*
   * Jam masuk yang tampak di MASA DEPAN berarti jam perangkat menyimpang dari server. Tetap
   * ditanyakan — bukan didiamkan — karena aksi ini yang mahal bila keliru, tetapi kalimatnya
   * TIDAK menyebut angka: "baru masuk -4 menit lalu" membuat orang berhenti mempercayai seluruh
   * peringatannya, dan peringatan yang tidak dipercaya sama saja dengan tidak ada.
   */
  if (selisihDetik < 0) {
    return {
      perluKonfirmasi: true,
      detik: null,
      pesan:
        'Jam absen masuk hari ini belum dapat dibaca dengan pasti. Yakin sudah selesai bekerja?',
    };
  }

  if (selisihDetik >= AMBANG_DETIK) return TIDAK_PERLU;

  return {
    perluKonfirmasi: true,
    detik: selisihDetik,
    pesan:
      `Anda baru absen masuk ${lamaSingkat(selisihDetik)} lalu. ` +
      'Absen pulang sekarang membuat hari ini tercatat hampir tanpa jam kerja. ' +
      'Yakin sudah selesai bekerja?',
  };
}
